import { TpETLPipelineComponent, TpETLDryRunTableObject, TpETLDryRunMatrixObject } from '../interfaces';
import { getETLComponentType } from './factory';
import { TpETLComponentTypeConfigParamDef, TpETLComponentTypeManifest } from './interfaces';
import { TpMatrixColumnType } from '../etl-data-types/interfaces';

export function createConfigParamTypeNewColumn(dataType: string) {
  return {
    configParamTypeId: 'newTableColumn',
    dataType,
  };
}

export function createConfigParamTypeExistingColumn(inputTableId: string) {
  return {
    configParamTypeId: 'existingTableColumn',
    inputTableId,
  };
}

export function createConfigParamTypeExistingMatrixColumn(inputMatrixId: string, matrixColumnType: TpMatrixColumnType) {
  return {
    configParamTypeId: 'existingMatrixColumn',
    inputMatrixId,
    matrixColumnType,
  };
}

export function createConfigParamTypeExistingColumnList(inputTableId: string) {
  return {
    configParamTypeId: 'existingTableColumnList',
    inputTableId,
  };
}

export function createConfigParamTypeExistingMatrixColumnList(
  inputMatrixId: string,
  matrixColumnType: TpMatrixColumnType,
) {
  return {
    configParamTypeId: 'existingMatrixColumnList',
    inputMatrixId,
    matrixColumnType,
  };
}

export function createConfigParamTypeMultipleChoice(options: { id: string; name: string }[], defaultValue?: string) {
  return {
    configParamTypeId: 'multipleChoice',
    options: structuredClone(options),
    defaultValue,
  };
}

export function createConfigParamTypeMultipleChoiceList(
  options: { id: string; name: string }[],
  defaultValue?: string[],
) {
  return {
    configParamTypeId: 'multipleChoiceList',
    options: structuredClone(options),
    defaultValue,
  };
}

export function getETLComponentTypeParam(
  compType: TpETLComponentTypeManifest,
  paramId: string,
): TpETLComponentTypeConfigParamDef {
  for (const param of compType.params) if (param.id == paramId) return param;
  throw `Parameter ${paramId} not present in component type ${compType.id}`;
}

export function augmentETLDryRunErrorObject(error: any, compId: string | null, paramId: string | null) {
  let errorObject: any = {};
  if (error.message)
    errorObject = { ...error }; // already a proper error object
  else errorObject.message = String(error);
  if (compId) errorObject.compId = compId;
  if (paramId) errorObject.paramId = paramId;
  return errorObject;
}

export function copyETLDryRunObjectTableColumns(
  tableObjFrom: TpETLDryRunTableObject,
  tableObjTo: TpETLDryRunTableObject,
) {
  for (const col of tableObjFrom.columns) {
    tableObjTo.columns.push(structuredClone(col));
  }
}

function checkColumnUniqueness(tableObj: TpETLDryRunTableObject, colId: string) {
  for (const col of tableObj.columns)
    if (col.identifier == colId) {
      throw `Column with identifier "${colId}" already exists`;
    }
}

export function ETLDryRunObjectTableAddColumn(
  tableObj: TpETLDryRunTableObject,
  identifier: string,
  name: string,
  description: string | undefined,
  dataType: string,
) {
  if (!identifier) throw 'Column misses identifier';
  if (!name) throw 'Column misses name';
  if (!dataType) throw 'Column misses data type';
  checkColumnUniqueness(tableObj, identifier);
  tableObj.columns.push({
    identifier,
    name,
    description,
    dataType,
  });
}

export function ETLDryRunObjectTableAddColumnFromParam(
  tableObj: TpETLDryRunTableObject,
  comp: TpETLPipelineComponent,
  columnParamId: string,
) {
  try {
    const compType = getETLComponentType(comp.componentType);
    const paramInfo = getETLComponentTypeParam(compType, columnParamId);
    if (paramInfo.paramType.configParamTypeId !== 'newTableColumn')
      throw `Parameter ${columnParamId} of ${compType.id} is not of type table column`;
    const paramVal = comp.params[columnParamId];
    if (!paramVal || !paramVal.identifier) throw `Column parameter "${paramInfo.name}" has no identifier`;
    if (!paramVal.name) throw `Column parameter "${paramInfo.name}" has no name`;
    checkColumnUniqueness(tableObj, paramVal.identifier);
    tableObj.columns.push({
      identifier: paramVal.identifier,
      name: paramVal.name || '',
      description: paramVal.description || '',
      dataType: paramInfo.paramType.dataType as string,
    });
  } catch (error) {
    throw augmentETLDryRunErrorObject(error, comp.id, columnParamId);
  }
}

export function copyETLDryRunObjectMatrix(matrixFrom: TpETLDryRunMatrixObject, matrixTo: TpETLDryRunMatrixObject) {
  for (const col of matrixFrom.obsMetadataColumns) {
    matrixTo.obsMetadataColumns.push(structuredClone(col));
  }
  for (const col of matrixFrom.featureMetadataColumns) {
    matrixTo.featureMetadataColumns.push(structuredClone(col));
  }
  matrixTo.linkColumnId = matrixFrom.linkColumnId;
}
